export type PipelineBatchAccumulator<T> = {
  expectedBatchCount?: number
  readonly batches: Array<readonly T[] | undefined>
}

function validBatchCount(value: number): boolean {
  return Number.isInteger(value) && value > 0
}

/** Records one worker batch while rejecting duplicates and contradictory metadata. */
export function acceptPipelineBatch<T>(
  accumulator: PipelineBatchAccumulator<T>,
  batchIndex: number,
  batchCount: number,
  items: readonly T[],
): void {
  if (!validBatchCount(batchCount)) throw new Error(`World pipeline declared invalid batch count ${batchCount}.`)
  if (!Number.isInteger(batchIndex) || batchIndex < 0 || batchIndex >= batchCount) {
    throw new Error(`World pipeline delivered out-of-range batch ${batchIndex}/${batchCount}.`)
  }
  if (accumulator.expectedBatchCount !== undefined && accumulator.expectedBatchCount !== batchCount) {
    throw new Error(`World pipeline changed batch count from ${accumulator.expectedBatchCount} to ${batchCount}.`)
  }
  if (items.length === 0) throw new Error(`World pipeline delivered empty batch ${batchIndex}/${batchCount}.`)
  if (accumulator.batches[batchIndex] !== undefined) throw new Error(`World pipeline delivered duplicate batch ${batchIndex}/${batchCount}.`)
  accumulator.expectedBatchCount = batchCount
  accumulator.batches[batchIndex] = items
}

/**
 * Completes a transaction only when every declared batch is present exactly
 * once and every item identity is unique.
 */
export function completePipelineBatches<T>(
  accumulator: PipelineBatchAccumulator<T>,
  batchCount: number,
  identity: (item: T) => string,
): T[] {
  if (!validBatchCount(batchCount)) throw new Error(`World pipeline declared invalid final batch count ${batchCount}.`)
  if (accumulator.expectedBatchCount !== batchCount) {
    throw new Error(`World pipeline finalized ${batchCount} batches after declaring ${accumulator.expectedBatchCount ?? 0}.`)
  }
  const result: T[] = []
  const identities = new Set<string>()
  for (let batchIndex = 0; batchIndex < batchCount; batchIndex += 1) {
    const batch = accumulator.batches[batchIndex]
    if (!batch) throw new Error(`World pipeline finalized without batch ${batchIndex}/${batchCount}.`)
    for (const item of batch) {
      const key = identity(item)
      if (identities.has(key)) throw new Error(`World pipeline delivered duplicate item ${key}.`)
      identities.add(key)
      result.push(item)
    }
  }
  if (result.length === 0) throw new Error('World pipeline finalized an empty macrotile.')
  return result
}
